import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApiQuery } from "@/lib/useApiQuery";
import { Layers, Zap } from "lucide-react";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { NoProjectAccess } from "@/components/AppShell";

function formatArea(area) {
  if (!area) return "—";
  if (typeof area === "object" && area.val != null) return `${Number(area.val).toLocaleString()} ${area.unit || ""}`.trim();
  return String(area);
}

export default function SitesPage() {
  const { project } = useProject();
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useMemo(() => (project ? { project } : {}), [project]);
  const { data, loading } = useApiQuery(["sites", params], "/sites", { params });
  const sites = useMemo(() => data || [], [data]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const s of sites) {
      const key = s.project || "Unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    let idx = -1;
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([project, list]) => [project, list.map((s) => { idx += 1; return { site: s, rowIndex: idx }; })]);
  }, [sites]);

  if (user?.role !== "admin" && (user?.projects || []).length === 0) return <NoProjectAccess />;

  return (
    <div className="max-w-7xl fade-up">
      <div className="k-label">Portfolio</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]">All sites</h1>
      <p className="mt-3 text-[#4D4D4D] text-sm">
        {loading ? "Loading…" : `${sites.length} site${sites.length === 1 ? "" : "s"} across ${groups.length} project${groups.length === 1 ? "" : "s"}`}
      </p>

      {loading ? (
        <div className="mt-8 k-card p-6 k-sublabel">Loading…</div>
      ) : sites.length === 0 ? (
        <div className="mt-8 k-card p-6 text-[#6B7280] text-sm" data-testid="sites-empty">
          No sites synced yet.
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          {groups.map(([project, list]) => (
            <div key={project} data-testid={`sites-project-group-${project}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-[#156F5B]" />
                  <h2 className="text-lg font-heading font-bold text-[#212121]">{project}</h2>
                  <span className="text-[11px] font-heading font-semibold text-[#156F5B] bg-[#156F5B]/10 border border-[#156F5B]/20 rounded-full px-2 py-0.5">
                    {list.length} site{list.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>

              <div className="mt-3 k-card overflow-hidden">
                <div className="grid grid-cols-12 px-6 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <div className="col-span-5 k-sublabel">Site</div>
                  <div className="col-span-3 k-sublabel">Location</div>
                  <div className="col-span-2 k-sublabel">Area</div>
                  <div className="col-span-2 k-sublabel text-right">Energy</div>
                </div>
                {list.map(({ site: s, rowIndex }) => (
                    <Link
                      key={s.site_id}
                      to={`/sites/${encodeURIComponent(s.site_id)}`}
                className="grid grid-cols-12 px-6 py-4 border-b border-[#F0F2F1] last:border-0 hover:bg-[#F3F8F7]"
                style={{ transition: "background-color .2s ease" }}
                data-testid={`sites-row-${rowIndex}`}
              >
                <div className="col-span-5">
                  <div className="text-[#212121] font-heading font-semibold">{s.dis}</div>
                  <div className="text-[11px] text-[#9CA3AF] mt-1 break-all">{s.site_id}</div>
                </div>
                <div className="col-span-3 text-sm text-[#4D4D4D]">
                  {[s.geo_city, s.geo_state].filter(Boolean).join(", ") || "—"}
                </div>
                <div className="col-span-2 text-sm text-[#4D4D4D]">{formatArea(s.area)}</div>
                <div className="col-span-2 text-right">
                  <span
                    role="link"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/sites/${encodeURIComponent(s.site_id)}/energy`);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/sites/${encodeURIComponent(s.site_id)}/energy`);
                      }
                    }}
                    data-testid={`sites-energy-link-${rowIndex}`}
                    className="inline-flex items-center gap-1 text-[11px] font-heading font-semibold text-[#156F5B] hover:underline cursor-pointer"
                  >
                    <Zap className="h-3 w-3" /> Energy &amp; cost
                  </span>
                </div>
              </Link>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
